import {Injectable} from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    CanActivateChild,
    Route,
    Router,
    RouterStateSnapshot,
    UrlSegment,
    UrlTree
} from '@angular/router';
import {AuthService} from '@shared/services/auth.service';
import {Observable} from 'rxjs';

/**
 * Locks the app for users who were required to enable 2FA but missed the deadline.
 *
 * Same shape as BannedToUpgradeGuard: purely client-side, evaluated from the policy document that
 * AuthService keeps in memory, with an allowlist of routes that stay reachable while locked.
 *
 * If the policy has not arrived yet the guard lets the user pass — AuthService redirects as soon
 * as the document is in, which avoids a redirect flash on every cold page load.
 */
@Injectable({
    providedIn: 'root'
})
export class TwoFactorRequiredGuard implements CanActivate, CanActivateChild {

    constructor(private authService: AuthService,
                private router: Router) {
    }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        return this.isNotLockedByTwoFactorPolicy(state?.url);
    }

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        return this.isNotLockedByTwoFactorPolicy(state?.url);
    }

    canLoad(
        route: Route,
        segments: UrlSegment[]): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
        return this.isNotLockedByTwoFactorPolicy(`/${segments.map((segment) => segment.path).join('/')}`);
    }

    isNotLockedByTwoFactorPolicy(targetUrl?: string): boolean | UrlTree {
        if (!this.authService.is2FALockActive() || this.authService.isTwoFactorRecoveryRoute(targetUrl)) {
            return true;
        }

        return this.router.createUrlTree(['fehler', '423']);
    }
}
